import React, { useState } from 'react';
import { useApp } from '../App';
import { fsSet, fsDelete, fsLogChange } from '../lib/firebase';
import { getRoutines, MAX_ROUTINES } from '../lib/pickups';
import { syncHomeschoolStudent } from '../lib/tokens';
import { AccordionSection } from '../components/ui/Accordion';
import { scheduleBatchedPublish } from '../api/schedulePublish';

// ── Edit Children ─────────────────────────────────────────────────────────────

const ROUTINE_DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr'];

// A drop-off/pickup leg — days/adults live here; `enabled` gates whether it runs.
function makeLeg(defaults = {}) {
  return {
    enabled: false, time: '3:15 PM', adult_pattern: 'all_week',
    adult_all_week: '', adult_mwf: '', adult_tth: '', adult_days: {},
    ...defaults,
  };
}

function makeRoutine(id) {
  return {
    id: id || `rt_${Date.now()}`,
    label: 'School',
    days: [...ROUTINE_DAYS],
    dropoff: makeLeg({ enabled: false, time: '8:00 AM' }),
    pickup:  makeLeg({ enabled: true,  time: '3:15 PM' }),
  };
}

// Editable routines for a child — real ones, or one synthesised from the
// legacy flat fields (via getRoutines). Both legs are always present as
// objects (with an `enabled` flag) so the editor can toggle them.
function routinesForEdit(child) {
  const routines = getRoutines(child);
  if (!routines.length) return [makeRoutine()];
  return routines.map(r => ({
    id: r.id || `rt_${Date.now()}`,   // keep 'rt_legacy' stable; don't churn ids
    label: r.label || 'School',
    days: r.days?.length ? [...r.days] : [...ROUTINE_DAYS],
    dropoff: r.dropoff ? makeLeg({ ...r.dropoff, enabled: r.dropoff.enabled !== false }) : makeLeg({ enabled: false, time: '8:00 AM' }),
    pickup:  r.pickup  ? makeLeg({ ...r.pickup,  enabled: r.pickup.enabled  !== false }) : makeLeg({ enabled: false }),
  }));
}

// Persisted shape — drop disabled legs to null, drop routines with no leg.
function routinesForSave(routines) {
  const strip = leg => ({
    enabled: true, time: leg.time || '',
    adult_pattern: leg.adult_pattern || 'all_week',
    adult_all_week: leg.adult_all_week || '', adult_mwf: leg.adult_mwf || '',
    adult_tth: leg.adult_tth || '', adult_days: leg.adult_days || {},
  });
  return (routines || [])
    .map(r => ({
      id: r.id,
      label: (r.label || '').trim() || 'Pickup',
      days: r.days?.length ? r.days : [...ROUTINE_DAYS],
      dropoff: r.dropoff?.enabled ? strip(r.dropoff) : null,
      pickup:  r.pickup?.enabled  ? strip(r.pickup)  : null,
    }))
    .filter(r => r.dropoff || r.pickup);
}

export default function EditChildren({ onBack }) {
  const { familyData, setFamilyData } = useApp() || {};
  const [children, setChildren]   = useState(familyData?.children || []);
  const [editingId, setEditingId] = useState(null); // child.id | '__new__' | null
  const [form, setForm]           = useState({});
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Insurance provider/member id are owner-only (private/insurance doc);
  // legacy on-child fields are the fallback until the migration runs
  const insuranceMap = familyData?.houseData?.insurance || {};

  const EMPTY_FORM = {
    // Identity
    firstName: '', lastName: '', preferred_name: '',
    date_of_birth: '', dob: '', photo: '',
    // Medical — critical
    allergy_list: '',           // comma-separated
    allergy_severity: '',       // comma-separated mild|moderate|severe per allergy
    epipen_on_premises: false,
    epipen_location: '',
    medical_conditions: '',     // comma-separated, 'None' if absent
    blood_type: '',
    // Medications
    medications: '',            // legacy flat field
    current_medications: '',    // comma-separated meds for display
    // Doctor
    physician_name: '', physician_phone: '', after_hours_nurse_line: '',
    insurance_provider: '', insurance_member_id: '', insurance_card_location: '',
    // Routine
    bedtime: '', bedtime_routine_notes: '',
    dietary_restrictions: '', screen_time_rules: '', behavioral_notes: '',
    // Legacy
    allergies: '', medNotes: '', notes: '',
    // Pickup — up to MAX_ROUTINES named drop-off/pickup routines per child
    pickup_enabled: true,
    pickup_routines: [makeRoutine()],
    // Schooling — 'homeschool' unlocks the school module for studentEmail,
    // which must be that child's own Google account (see lib/tokens.js
    // syncHomeschoolStudent)
    schoolType: 'public',
    studentEmail: '',
  };

  function startAdd() {
    setEditingId('__new__');
    setForm(EMPTY_FORM);
  }

  function startEdit(child) {
    setEditingId(child.id);
    setForm({
      ...EMPTY_FORM,
      ...child,
      pickup_routines: routinesForEdit(child),
      insurance_provider:  insuranceMap[child.id]?.provider  || child.insurance_provider || '',
      insurance_member_id: insuranceMap[child.id]?.memberId  || child.insurance_member_id || '',
      allergy_list: Array.isArray(child.allergy_list)
        ? child.allergy_list.filter(x => x !== 'None').join(', ')
        : (child.allergy_list || ''),
      allergy_severity: Array.isArray(child.allergy_severity)
        ? child.allergy_severity.join(', ')
        : (child.allergy_severity || ''),
      medical_conditions: Array.isArray(child.medical_conditions)
        ? child.medical_conditions.filter(x => x !== 'None').join(', ')
        : (child.medical_conditions || ''),
      current_medications: Array.isArray(child.current_medications)
        ? child.current_medications.map(m => m.name || m).filter(Boolean).join(', ')
        : (child.current_medications || ''),
      // Legacy children docs predate the school module — default to public
      schoolType: child.schoolType || 'public',
      studentEmail: child.studentEmail || '',
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm({});
  }

  async function saveChild() {
    if (!form.firstName.trim() || !familyData?.id) return;
    setSaving(true);
    setSaveError(null);
    try {
      await saveChildInner();
    } catch (err) {
      console.error('[settings] save child failed:', err);
      setSaveError(err?.message || 'Save failed — check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  async function saveChildInner() {
    const id = editingId === '__new__' ? `child_${Date.now()}` : editingId;
    // Previous persisted state — needed to know which email to drop from
    // families.homeschoolStudentEmails if it changed or schooling was toggled off
    const existingChild = editingId === '__new__' ? null : children.find(c => c.id === id);
    const child = {
      id,
      // Identity
      firstName:          form.firstName.trim(),
      lastName:           form.lastName.trim(),
      preferred_name:     form.preferred_name.trim() || form.firstName.trim(),
      date_of_birth:      form.date_of_birth || form.dob,
      dob:                form.date_of_birth || form.dob,
      // Medical
      allergy_list:       form.allergy_list ? form.allergy_list.split(',').map(s=>s.trim()).filter(Boolean) : ['None'],
      allergy_severity:   form.allergy_severity ? form.allergy_severity.split(',').map(s=>s.trim()) : [],
      epipen_on_premises: form.epipen_on_premises || false,
      epipen_location:    form.epipen_location.trim(),
      medical_conditions: form.medical_conditions ? form.medical_conditions.split(',').map(s=>s.trim()).filter(Boolean) : ['None'],
      blood_type:         form.blood_type || '',
      // Meds
      medications:        form.medications.trim(),
      current_medications: form.current_medications ? form.current_medications.split(',').map(m=>({ name: m.trim() })).filter(m=>m.name) : [],
      // Doctor
      physician_name:         form.physician_name.trim(),
      physician_phone:        form.physician_phone.trim(),
      after_hours_nurse_line: form.after_hours_nurse_line.trim(),
      // provider/member id live in owner-only private/insurance — blank the
      // legacy on-child copies so token viewers can't read them
      insurance_provider:     '',
      insurance_member_id:    '',
      insurance_card_location:form.insurance_card_location.trim(),
      // Routine
      bedtime:                form.bedtime.trim(),
      bedtime_routine_notes:  form.bedtime_routine_notes.trim(),
      dietary_restrictions:   form.dietary_restrictions.trim(),
      screen_time_rules:      form.screen_time_rules.trim(),
      behavioral_notes:       form.behavioral_notes.trim(),
      notes:                  form.notes.trim(),
      // Pickup — routines model (legacy flat pickup_* fields are no longer written)
      pickup_enabled:         form.pickup_enabled !== false,
      pickup_routines:        routinesForSave(form.pickup_routines),
      // Schooling — homeschool unlocks the school module for this child
      schoolType:              form.schoolType === 'homeschool' ? 'homeschool' : 'public',
      studentEmail:            form.studentEmail.trim(),
    };
    await fsSet(`families/${familyData.id}/children/${id}`, child);
    // Keep the family-level rules index in sync with this child's schooling
    // toggle/email — see lib/tokens.js syncHomeschoolStudent
    await syncHomeschoolStudent({
      familyId: familyData.id,
      email: child.studentEmail,
      previousEmail: existingChild?.studentEmail,
      enabled: child.schoolType === 'homeschool' && !!child.studentEmail,
      familyData,
      setFamilyData,
    });
    // Insurance ids go to the owner-only private/insurance doc
    const insEntry = { provider: form.insurance_provider.trim(), memberId: form.insurance_member_id.trim() };
    const { saveInsurance } = await import('../lib/houseData');
    await saveInsurance(familyData.id, { [id]: insEntry });
    await fsLogChange(familyData.id, {
      actorId: familyData.creatorEmail, actorName: familyData.creatorEmail,
      module: 'settings', action: editingId === '__new__' ? 'add_child' : 'edit_child',
      entityId: id, toValue: child.firstName,
    });
    const updated = editingId === '__new__'
      ? [...children, child]
      : children.map(c => c.id === id ? child : c);
    setChildren(updated);
    setFamilyData(prev => ({
      ...prev,
      children: updated,
      houseData: {
        ...(prev.houseData || {}),
        insurance: { ...(prev.houseData?.insurance || {}), [id]: insEntry },
      },
    }));
    // Pickup routines feed the published schedule's pickup rows — queue a
    // republish (batched with anything else happening right now) so a routine
    // change doesn't sit stale for caregivers until the next Lock In.
    scheduleBatchedPublish({ ...familyData, children: updated });
    cancelEdit();
  }

  async function removeChild(child) {
    if (!window.confirm(`Remove ${child.firstName}?`)) return;
    await fsDelete(`families/${familyData.id}/children/${child.id}`);
    await fsLogChange(familyData.id, {
      actorId: familyData.creatorEmail, actorName: familyData.creatorEmail,
      module: 'settings', action: 'remove_child', entityId: child.id, toValue: child.firstName,
    });
    const updated = children.filter(c => c.id !== child.id);
    setChildren(updated);
    setFamilyData(prev => ({ ...prev, children: updated }));
  }

  return (
    <div className="family-edit-panel">
      <button className="btn btn--back-sm" onClick={onBack}>← Back</button>
      <h3>Children</h3>
      <div className="edit-list">
        {children.map(c => {
          const isEditing = editingId === c.id;
          return (
            <div key={c.id} className={`edit-list-row ${isEditing ? 'edit-list-row--editing' : ''}`}>
              {!isEditing ? (
                <>
                  <div className="edit-list-row__info">
                    <span className="edit-list-row__id-badge">{c.firstName[0]}</span>
                    <div>
                      <div className="edit-list-row__name">{c.firstName} {c.lastName}</div>
                      <div className="edit-list-row__sub">
                        {[
                          c.dob && `DOB: ${c.dob}`,
                          c.allergies && `🔥 ${c.allergies}`,
                          c.bedtime && `🌙 ${c.bedtime}`,
                          (() => { const rs = getRoutines(c); return rs.length ? `🚌 ${rs.map(r => r.label).join(', ')}` : null; })(),
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="btn btn--ghost btn--small" onClick={() => startEdit(c)}>Edit</button>
                    <button className="btn btn--ghost btn--small btn--danger-ghost" onClick={() => removeChild(c)}>Remove</button>
                  </div>
                </>
              ) : (
                <div style={{ flex: 1 }}>
                  <ChildForm form={form} setForm={setForm} onSave={saveChild} onCancel={cancelEdit} saving={saving} saveError={saveError} autoFocus />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editingId === '__new__' ? (
        <ChildForm form={form} setForm={setForm} onSave={saveChild} onCancel={cancelEdit} saving={saving} saveError={saveError} autoFocus />
      ) : (
        <button className="btn btn--secondary" style={{ marginTop: 8 }} onClick={startAdd}>+ Add Child</button>
      )}
    </div>
  );
}

// ── CHILD FORM ────────────────────────────────────────────────────────────────
// Top-level (module-scope) so its component identity is stable across parent
// re-renders — defining this inside EditChildren's body used to recreate the
// function on every keystroke, causing React to unmount/remount the whole
// form (and re-fire the always-on autoFocus on the top field) on every
// character typed.
function ChildForm({ form, setForm, onSave, onCancel, saving, saveError, autoFocus }) {
  const { familyData } = useApp() || {};
  const [openSection, setOpenSection] = useState(null); // accordion — only one group open at a time

  // Pickup-eligible caregivers join parents in the pickup-adult pickers,
  // stored as 'cg:{sitterId}' (lib/pickups.js resolves both id forms)
  const pickupCaregivers = (familyData?.sitters || []).filter(s => s.pickupEligible && s.active !== false);

  // ── Pickup-routine editor state helpers ──
  const setRoutines = updater => setForm(f => ({ ...f, pickup_routines: updater(f.pickup_routines || []) }));
  const updateRoutine = (ri, patch) => setRoutines(rs => rs.map((r, i) => i === ri ? { ...r, ...patch } : r));
  const updateLeg = (ri, leg, patch) => setRoutines(rs => rs.map((r, i) => i === ri ? { ...r, [leg]: { ...r[leg], ...patch } } : r));
  const toggleRoutineDay = (ri, d) => setRoutines(rs => rs.map((r, i) => {
    if (i !== ri) return r;
    const cur = r.days || [];
    return { ...r, days: cur.includes(d) ? cur.filter(x => x !== d) : [...cur, d] };
  }));
  const addRoutine = () => setRoutines(rs => rs.length >= MAX_ROUTINES ? rs : [...rs, makeRoutine(`rt_${Date.now()}_${rs.length}`)]);
  const removeRoutine = ri => setRoutines(rs => rs.filter((_, i) => i !== ri));

  // Care-partner + pickup-eligible-caregiver pills for one adult slot.
  function renderAdultPills(active, onPick, small = false) {
    const cls = `pickup-cp-pill${small ? ' pickup-cp-pill--sm' : ''}`;
    return (
      <>
        {(familyData?.carePartners || []).map(cp => {
          const val = cp.identifier || cp.id;
          return (
            <button key={cp.id} type="button" className={`${cls} ${active === val ? 'active' : ''}`}
              onClick={() => onPick(val)}>{cp.name}</button>
          );
        })}
        {pickupCaregivers.map(s => {
          const val = `cg:${s.id}`;
          return (
            <button key={`cg-${s.id}`} type="button" className={`${cls} ${active === val ? 'active' : ''}`}
              onClick={() => onPick(val)}>{s.firstName} 🌱</button>
          );
        })}
      </>
    );
  }

  // One drop-off or pickup leg of a routine — enable toggle, time, adult pattern.
  function renderLeg(ri, leg, legData, days) {
    const legName = leg === 'dropoff' ? 'Drop-off' : 'Pickup';
    return (
      <div className="pickup-leg">
        <label className="pickup-toggle-row">
          <input type="checkbox" checked={!!legData.enabled}
            onChange={e => updateLeg(ri, leg, { enabled: e.target.checked })} />
          <span>Include {legName.toLowerCase()}</span>
        </label>
        {legData.enabled && (
          <>
            <div className="form-label" style={{ marginTop: 8 }}>{legName} time</div>
            <input className="input input--short" placeholder="e.g. 3:15 PM" value={legData.time || ''}
              onChange={e => updateLeg(ri, leg, { time: e.target.value })} />
            <div className="form-label" style={{ marginTop: 8 }}>{legName} adult</div>
            <div className="pickup-pattern-tabs">
              {[
                { id: 'all_week', label: 'Same all week' },
                { id: 'mwf_tth',  label: 'MWF / T–TH split' },
                { id: 'specific', label: 'Specific days' },
              ].map(tab => (
                <button key={tab.id} type="button"
                  className={`pickup-pattern-tab ${(legData.adult_pattern || 'all_week') === tab.id ? 'active' : ''}`}
                  onClick={() => updateLeg(ri, leg, { adult_pattern: tab.id })}>{tab.label}</button>
              ))}
            </div>
            {(legData.adult_pattern || 'all_week') === 'all_week' && (
              <div className="pickup-adult-row">
                <span className="pickup-adult-row__label">Every day</span>
                <div className="pickup-cp-pills">{renderAdultPills(legData.adult_all_week, v => updateLeg(ri, leg, { adult_all_week: v }))}</div>
              </div>
            )}
            {legData.adult_pattern === 'mwf_tth' && (
              <div className="pickup-split-rows">
                <div className="pickup-adult-row">
                  <span className="pickup-adult-row__label">Mon · Wed · Fri</span>
                  <div className="pickup-cp-pills">{renderAdultPills(legData.adult_mwf, v => updateLeg(ri, leg, { adult_mwf: v }))}</div>
                </div>
                <div className="pickup-adult-row">
                  <span className="pickup-adult-row__label">Tue · Thu</span>
                  <div className="pickup-cp-pills">{renderAdultPills(legData.adult_tth, v => updateLeg(ri, leg, { adult_tth: v }))}</div>
                </div>
              </div>
            )}
            {legData.adult_pattern === 'specific' && (
              <div className="pickup-specific-grid">
                {(days?.length ? days : ROUTINE_DAYS).map(d => (
                  <div key={d} className="pickup-specific-row">
                    <span className="pickup-specific-row__day">{d}</span>
                    <div className="pickup-cp-pills">
                      {renderAdultPills((legData.adult_days || {})[d], v => updateLeg(ri, leg, { adult_days: { ...(legData.adult_days || {}), [d]: v } }), true)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="child-edit-form">
      <div className="form-row">
        <div>
          <div className="form-label" style={{ marginTop: 0 }}>First name</div>
          <input className="input" value={form.firstName} autoFocus={autoFocus}
            onChange={e => setForm(f => ({...f, firstName: e.target.value}))} />
        </div>
        <div>
          <div className="form-label" style={{ marginTop: 0 }}>Last name</div>
          <input className="input" value={form.lastName}
            onChange={e => setForm(f => ({...f, lastName: e.target.value}))} />
        </div>
      </div>
      <div className="form-label">Preferred / nickname <span className="cp-edit-form__hint">(optional)</span></div>
      <input className="input" value={form.preferred_name}
        onChange={e => setForm(f => ({...f, preferred_name: e.target.value}))} />
      <div className="form-label">Date of birth</div>
      <input className="input" type="date" value={form.date_of_birth || form.dob}
        onChange={e => setForm(f => ({...f, date_of_birth: e.target.value, dob: e.target.value}))} />

      <AccordionSection id="medical" icon="🔥" title="Medical — Critical" openId={openSection} onToggle={setOpenSection}>
        <div className="form-label" style={{ marginTop: 0 }}>Allergies</div>
        <input className="input" placeholder="Comma-separated, or 'None'" value={form.allergy_list}
          onChange={e => setForm(f => ({...f, allergy_list: e.target.value}))} />
        <div className="form-label">Severity per allergy</div>
        <input className="input" placeholder="mild, moderate, severe — match order above" value={form.allergy_severity}
          onChange={e => setForm(f => ({...f, allergy_severity: e.target.value}))} />
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'4px 0'}}>
          <label style={{fontSize:13,color:'var(--forest-dark)',display:'flex',alignItems:'center',gap:6,cursor:'pointer'}}>
            <input type="checkbox" checked={form.epipen_on_premises}
              onChange={e => setForm(f => ({...f, epipen_on_premises: e.target.checked}))} />
            EpiPen on premises
          </label>
        </div>
        {form.epipen_on_premises && (
          <>
            <div className="form-label">EpiPen location</div>
            <input className="input" placeholder="💉 e.g. Kitchen drawer, left of sink" value={form.epipen_location}
              onChange={e => setForm(f => ({...f, epipen_location: e.target.value}))} />
          </>
        )}
        <div className="form-label">Medical conditions</div>
        <input className="input" placeholder="Comma-separated, or 'None'" value={form.medical_conditions}
          onChange={e => setForm(f => ({...f, medical_conditions: e.target.value}))} />
        <div className="form-label">Blood type <span className="cp-edit-form__hint">(optional)</span></div>
        <select className="input" value={form.blood_type} onChange={e => setForm(f => ({...f, blood_type: e.target.value}))}>
          <option value="">—</option>
          {['A+','A-','B+','B-','AB+','AB-','O+','O-','unknown'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </AccordionSection>

      <AccordionSection id="medications" icon="💊" title="Medications" openId={openSection} onToggle={setOpenSection}>
        <div className="form-label" style={{ marginTop: 0 }}>Medications</div>
        <textarea className="input" placeholder="Name, dose, frequency, time (one per line)" rows={3} value={form.medications}
          onChange={e => setForm(f => ({...f, medications: e.target.value}))} />
      </AccordionSection>

      <AccordionSection id="doctor" icon="🌿" title="Doctor & Insurance" openId={openSection} onToggle={setOpenSection}>
        <div className="form-label" style={{ marginTop: 0 }}>Physician name</div>
        <input className="input" value={form.physician_name}
          onChange={e => setForm(f => ({...f, physician_name: e.target.value}))} />
        <div className="form-label">Physician / after-hours phone</div>
        <input className="input" value={form.physician_phone}
          onChange={e => setForm(f => ({...f, physician_phone: e.target.value}))} />
        <div className="form-label">Nurse line <span className="cp-edit-form__hint">(if separate)</span></div>
        <input className="input" value={form.after_hours_nurse_line}
          onChange={e => setForm(f => ({...f, after_hours_nurse_line: e.target.value}))} />
        <div className="form-label">Insurance provider</div>
        <input className="input" placeholder="e.g. Horizon BCBS NJ" value={form.insurance_provider}
          onChange={e => setForm(f => ({...f, insurance_provider: e.target.value}))} />
        <div className="form-label">Member ID</div>
        <input className="input" value={form.insurance_member_id}
          onChange={e => setForm(f => ({...f, insurance_member_id: e.target.value}))} />
        <div className="form-label">Insurance card location in home</div>
        <input className="input" value={form.insurance_card_location}
          onChange={e => setForm(f => ({...f, insurance_card_location: e.target.value}))} />
      </AccordionSection>

      <AccordionSection id="routine" icon="🌙" title="Routine & Behaviour" openId={openSection} onToggle={setOpenSection}>
        <div className="form-label" style={{ marginTop: 0 }}>Bedtime</div>
        <input className="input" placeholder="e.g. 8:00 PM" value={form.bedtime}
          onChange={e => setForm(f => ({...f, bedtime: e.target.value}))} />
        <div className="form-label">Bedtime routine notes</div>
        <textarea className="input" rows={2} value={form.bedtime_routine_notes}
          onChange={e => setForm(f => ({...f, bedtime_routine_notes: e.target.value}))} />
        <div className="form-label">Dietary restrictions <span className="cp-edit-form__hint">(non-allergy)</span></div>
        <textarea className="input" rows={2} value={form.dietary_restrictions}
          onChange={e => setForm(f => ({...f, dietary_restrictions: e.target.value}))} />
        <div className="form-label">Screen time rules</div>
        <textarea className="input" rows={2} value={form.screen_time_rules}
          onChange={e => setForm(f => ({...f, screen_time_rules: e.target.value}))} />
        <div className="form-label">Behavioural notes <span className="cp-edit-form__hint">(sensory, fears, comfort items)</span></div>
        <textarea className="input" rows={3} value={form.behavioral_notes}
          onChange={e => setForm(f => ({...f, behavioral_notes: e.target.value}))} />
        <div className="form-label">General notes for sitters</div>
        <textarea className="input" rows={2} value={form.notes}
          onChange={e => setForm(f => ({...f, notes: e.target.value}))} />
      </AccordionSection>

      <AccordionSection id="school" icon="🎓" title="Schooling" openId={openSection} onToggle={setOpenSection}>
        <div className="pickup-pattern-tabs">
          {[
            { id: 'public', label: 'Public / other school' },
            { id: 'homeschool', label: 'Homeschool' },
          ].map(tab => (
            <button key={tab.id} type="button"
              className={`pickup-pattern-tab ${(form.schoolType || 'public') === tab.id ? 'active' : ''}`}
              onClick={() => setForm(f => ({ ...f, schoolType: tab.id }))}>{tab.label}</button>
          ))}
        </div>
        {form.schoolType === 'homeschool' && (
          <>
            <div className="form-label" style={{ marginTop: 8 }}>Student's Google account</div>
            <input className="input" type="email" placeholder="their-account@gmail.com" value={form.studentEmail}
              onChange={e => setForm(f => ({ ...f, studentEmail: e.target.value }))} />
            <p className="settings-note" style={{ marginTop: 8 }}>
              Must be a Google account this child can already sign in with — the school module reuses
              your family's Google sign-in, no separate password.
            </p>
          </>
        )}
      </AccordionSection>

      <AccordionSection id="pickup" icon="🚌" title="Drop-off & Pickup" openId={openSection} onToggle={setOpenSection}>
        {/* Master on/off — off suppresses all of this child's routines */}
        <label className="pickup-toggle-row">
          <input type="checkbox" checked={form.pickup_enabled !== false}
            onChange={e => setForm(f => ({...f, pickup_enabled: e.target.checked}))} />
          <span>This child has drop-off / pickup routines</span>
        </label>

        {form.pickup_enabled !== false && (<>
          <p className="settings-note" style={{ marginTop: 8, marginBottom: 8 }}>
            Up to {MAX_ROUTINES} routines (e.g. School, Aftercare). Each can have a drop-off and a pickup leg.
            These show up as reminders and assignments in Grove — they're never added to Google Calendar.
          </p>

          {(form.pickup_routines || []).map((routine, ri) => (
            <div key={routine.id} className="routine-editor">
              <div className="routine-editor__head">
                <input className="input" placeholder="Routine name (e.g. School)" value={routine.label || ''}
                  onChange={e => updateRoutine(ri, { label: e.target.value })} />
                {(form.pickup_routines || []).length > 1 && (
                  <button type="button" className="btn btn--ghost btn--small" onClick={() => removeRoutine(ri)}>Remove</button>
                )}
              </div>

              <div className="form-label" style={{ marginTop: 8 }}>Days</div>
              <div className="pickup-days-row">
                {ROUTINE_DAYS.map(d => (
                  <button key={d} type="button"
                    className={`pickup-day-btn ${(routine.days || []).includes(d) ? 'active' : ''}`}
                    onClick={() => toggleRoutineDay(ri, d)}>{d}</button>
                ))}
              </div>

              {renderLeg(ri, 'dropoff', routine.dropoff, routine.days)}
              {renderLeg(ri, 'pickup', routine.pickup, routine.days)}
            </div>
          ))}

          {(form.pickup_routines || []).length < MAX_ROUTINES && (
            <button type="button" className="btn btn--secondary btn--small" onClick={addRoutine}>+ Add routine</button>
          )}

          <p className="settings-note" style={{ marginTop: 8 }}>
            💡 Use the assignment tool on the Review & Assign screen to override a pickup for a specific day.
          </p>
        </>)}
      </AccordionSection>

      {saveError && (
        <p className="settings-note" style={{ color: 'var(--bad, #b00)' }}>{saveError}</p>
      )}
      <div className="add-form__actions">
        <button className="btn btn--primary" onClick={onSave} disabled={saving || !form.firstName.trim()}>
          {saving ? 'Saving…' : 'Save child'}
        </button>
        <button className="btn btn--ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}


